// philippinesV2Copy.ts
// ─── Astria Philippines v2 — bilingual (EN/PH) UI copy ─────────────────────
// All fixed UI strings for the shell header, tab bar, and the tab screen's
// chrome (loading/reset/submit/CTA text). The client-approved backend copy
// pack response text itself is NOT translated here — the matching language
// is selected on the backend via `wizard.lang` (see
// helper/astriaPhilippinesV2Service.js's EN_COPY_PACK/PH_COPY_PACK). This
// file only localizes the surrounding UI chrome. Same pattern as
// vietnamV2Copy.ts.
import { PhilippinesV2Lang } from "./philippinesV2Language";
import { AstriaPhilippinesV2Tab } from "./AstriaPhilippinesV2Shell";

interface ShellCopy {
  title: string;
  subtitle: string;
  langToggleLabel: string;
}

interface TabBarCopy {
  label: string;
  sublabel: string;
}

interface TabScreenCopy {
  question: string;
  helper: string;
  fields: Record<
    string,
    {
      label: string;
      options?: Record<string, string>;
      placeholder?: string;
      lowLabel?: string;
      highLabel?: string;
    }
  >;
}

interface PhilippinesV2CopyShape {
  shell: ShellCopy;
  tabBar: Record<AstriaPhilippinesV2Tab, TabBarCopy>;
  screens: Record<AstriaPhilippinesV2Tab, TabScreenCopy>;
  submitButton: string;
  loadingLabel: string;
  resetLink: string;
}

export const PHILIPPINES_V2_COPY: Record<PhilippinesV2Lang, PhilippinesV2CopyShape> = {
  ph: {
    shell: {
      title: "Astria Philippines",
      subtitle: "Isang mainit, magaan na espasyo para malaman ang nararamdaman mo",
      langToggleLabel: "Wika",
    },
    tabBar: {
      soft_summary: { label: "Malambot na Buod", sublabel: "Malambot na Buod" },
      connection_atmosphere: { label: "Atmosfera ng Koneksyon", sublabel: "Atmosfera ng Koneksyon" },
      timing_rhythm: { label: "Tono ng Oras", sublabel: "Tono ng Oras" },
      emotion_balance: { label: "Balanse ng Damdamin", sublabel: "Balanse ng Damdamin" },
      guidance_path: { label: "Magaan na Direksyon", sublabel: "Magaan na Direksyon" },
      your_note: { label: "Tala Mo", sublabel: "Tala Mo" },
    },
    screens: {
      soft_summary: {
        question: "Ano ang pakiramdam mo ngayon?",
        helper: "Isang magaan na check-in kung ano ang nararamdaman mo ngayon.",
        fields: {
          mood: { label: "Mood" },
          intensity: {
            label: "Gaano kalakas ang emosyon?",
            lowLabel: "Mahina",
            highLabel: "Malakas",
          },
        },
      },
      connection_atmosphere: {
        question: "Ano ang atmospera sa pagitan ninyo?",
        helper: "Ang init (o layo) na nararamdaman mo sa kanya.",
        fields: {
          connection: {
            label: "Atmospera sa pagitan ninyo",
            options: {
              warm: "Mainit",
              distant: "Malayo",
              tense: "Tense",
              playful: "Masaya",
              quiet: "Tahimik",
            },
          },
          tone: {
            label: "Tono ng usapan",
            options: {
              gentle: "Malambot",
              guarded: "Maingat",
              open: "Bukas",
              unsure: "Hindi sigurado",
            },
          },
        },
      },
      timing_rhythm: {
        question: "Ano ang tono ng oras ngayon?",
        helper: "Ang bilis at kahandaan ng sandaling ito.",
        fields: {
          flow: {
            label: "Tono ng timing",
            options: {
              slow: "Mabagal",
              steady: "Steady",
              rushed: "Rushed",
              uncertain: "Hindi sigurado",
            },
          },
          readiness: {
            label: "Gaano ka handa?",
            lowLabel: "Hindi pa",
            highLabel: "Handa na",
          },
        },
      },
      emotion_balance: {
        question: "Saan banda ang emosyon mo?",
        helper: "Kung saan naninirahan ang emosyon mo ngayon.",
        fields: {
          emotion_zone: {
            label: "Saan banda ang emosyon mo?",
            options: {
              warm: "Mainit",
              heavy: "Mabigat",
              light: "Magaan",
              tender: "Malambot",
              steady: "Steady",
            },
          },
          location: {
            label: "Anong pakiramdam ang nangingibabaw?",
            options: {
              chest: "Dibdib",
              mind: "Isip",
              stomach: "Tiyan",
              everywhere: "Lahat",
            },
          },
        },
      },
      guidance_path: {
        question: "Ano ang nangyayari?",
        helper: "Isang magaan na hakbang para sa hinaharap mo.",
        fields: {
          issue: {
            label: "Ano ang nangyayari?",
            options: {
              communication: "Komunikasyon",
              distance: "Layo",
              uncertainty: "Kawalang-katiyakan",
              overwhelm: "Sobrang bigat",
            },
          },
          desired: {
            label: "Ano ang gusto mong mangyari?",
            options: {
              clarity: "Kalinawan",
              closeness: "Kalapitan",
              peace: "Kapayapaan",
              courage: "Tapang",
            },
          },
        },
      },
      your_note: {
        question: "Anong nasa isip mo?",
        helper: "Isang magaan na repleksyon sa huling mensahe mo.",
        fields: {
          latest_message: {
            label: "Huling mensahe mo",
            placeholder: "Ano ang gusto mong sabihin?",
          },
          feeling: {
            label: "Ano ang naramdaman mo habang sinusulat ito?",
            options: {
              soft: "Malambot",
              hopeful: "Umaasa",
              unsure: "Hindi sigurado",
              relieved: "Gaan ng loob",
            },
          },
        },
      },
    },
    submitButton: "Ipakita",
    loadingLabel: "Binabasa ang check-in mo…",
    resetLink: "Magsimula ng bagong check-in",
  },
  en: {
    shell: {
      title: "Astria Philippines",
      subtitle: "A warm, gentle space to check in with how you're feeling",
      langToggleLabel: "Language",
    },
    tabBar: {
      soft_summary: { label: "Soft Summary", sublabel: "Malambot na Buod" },
      connection_atmosphere: { label: "Connection Atmosphere", sublabel: "Atmosfera ng Koneksyon" },
      timing_rhythm: { label: "Timing Rhythm", sublabel: "Tono ng Oras" },
      emotion_balance: { label: "Emotion Balance", sublabel: "Balanse ng Damdamin" },
      guidance_path: { label: "Guidance Path", sublabel: "Magaan na Direksyon" },
      your_note: { label: "Your Note", sublabel: "Tala Mo" },
    },
    screens: {
      soft_summary: {
        question: "How are you feeling right now?",
        helper: "A gentle check-in on how you're feeling right now.",
        fields: {
          mood: { label: "Mood" },
          intensity: {
            label: "How strong is that feeling?",
            lowLabel: "Light",
            highLabel: "Strong",
          },
        },
      },
      connection_atmosphere: {
        question: "What's the atmosphere between you two?",
        helper: "The warmth (or distance) you're feeling with someone else.",
        fields: {
          connection: {
            label: "Atmosphere between you two",
            options: {
              warm: "Warm",
              distant: "Distant",
              tense: "Tense",
              playful: "Playful",
              quiet: "Quiet",
            },
          },
          tone: {
            label: "Their tone",
            options: {
              gentle: "Gentle",
              guarded: "Guarded",
              open: "Open",
              unsure: "Unsure",
            },
          },
        },
      },
      timing_rhythm: {
        question: "What's the rhythm of this moment?",
        helper: "The pace and readiness of this moment.",
        fields: {
          flow: {
            label: "Current flow",
            options: {
              slow: "Slow",
              steady: "Steady",
              rushed: "Rushed",
              uncertain: "Uncertain",
            },
          },
          readiness: {
            label: "How ready do you feel?",
            lowLabel: "Not yet",
            highLabel: "Ready",
          },
        },
      },
      emotion_balance: {
        question: "Where are your emotions sitting?",
        helper: "Where your emotions are sitting right now.",
        fields: {
          emotion_zone: {
            label: "Emotion zone",
            options: {
              warm: "Warm",
              heavy: "Heavy",
              light: "Light",
              tender: "Tender",
              steady: "Steady",
            },
          },
          location: {
            label: "Where does it show up most?",
            options: {
              chest: "Chest",
              mind: "Mind",
              stomach: "Stomach",
              everywhere: "Everywhere",
            },
          },
        },
      },
      guidance_path: {
        question: "What's going on?",
        helper: "A soft next step for what you're facing.",
        fields: {
          issue: {
            label: "What's going on",
            options: {
              communication: "Communication",
              distance: "Distance",
              uncertainty: "Uncertainty",
              overwhelm: "Overwhelm",
            },
          },
          desired: {
            label: "What do you want?",
            options: {
              clarity: "Clarity",
              closeness: "Closeness",
              peace: "Peace",
              courage: "Courage",
            },
          },
        },
      },
      your_note: {
        question: "What's on your mind?",
        helper: "A gentle reflection on your latest message.",
        fields: {
          latest_message: {
            label: "Your latest message",
            placeholder: "What would you like to say?",
          },
          feeling: {
            label: "How did you feel while writing it?",
            options: {
              soft: "Soft",
              hopeful: "Hopeful",
              unsure: "Unsure",
              relieved: "Relieved",
            },
          },
        },
      },
    },
    submitButton: "Reveal",
    loadingLabel: "Reading your check-in…",
    resetLink: "Start a new check-in",
  },
};
